import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { homeFor } from "../lib/routes";
import { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, demoLogin, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("mama@bombacare.com");
  const [password, setPassword] = useState("Demo2026!");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate(homeFor(user), { replace: true }); }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Bienvenue !");
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bc-shell flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2BA3A0]" size={32} />
      </div>
    );
  }

  return (
    <div className="bc-shell" data-testid="login-page">
      <div className="flex-1 flex flex-col px-7 pt-12">
        <div className="flex items-center gap-3 mb-2">
          <Logo size={56} />
          <div>
            <div className="font-[Fraunces] text-2xl font-semibold text-[#0A1426] leading-none">
              Bombacare
            </div>
            <div className="text-[12px] text-[#0A1426]/70 mt-1">Connect · MVP1</div>
          </div>
        </div>
        <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426] mt-8 leading-tight">
          Reprenez votre vie en main, jour après jour
        </h1>
        <p className="text-[15px] text-[#0A1426]/75 mt-2">
          Rééducation, prévention et lien avec votre équipe — même hors ligne.
        </p>

        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
          <div>
            <Label htmlFor="email" className="text-[#0A1426] font-semibold">E-mail</Label>
            <Input
              id="email" type="email" autoComplete="email" required
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 text-base bg-white border-[#E4E0D5]"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-[#0A1426] font-semibold">Mot de passe</Label>
            <Input
              id="password" type="password" autoComplete="current-password" required
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-14 text-base bg-white border-[#E4E0D5]"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm text-[#D9534F] font-medium">{error}</p>
          )}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="h-14 text-base font-bold bg-[#2BA3A0] hover:bg-[#0A1426] text-white rounded-full"
          >
            {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Se connecter
          </Button>
          <Button type="button" data-testid="demo-mode-btn" variant="outline" disabled={submitting}
            onClick={async () => { try { setSubmitting(true); const u = await demoLogin(); navigate(homeFor(u), { replace: true }); } catch (e) { toast.error("Démo indisponible"); } finally { setSubmitting(false); } }}
            className="h-12 rounded-full border-[#0A1426] text-[#0A1426] font-bold">
            Mode démonstration (visite guidée)
          </Button>
          <Link to="/installer" data-testid="login-install-link" className="text-center text-[13px] font-bold text-[#2BA3A0] underline">Comment installer l'app sur mon téléphone ?</Link>
          <p className="text-center text-sm text-[#0A1426]/80">
            Pas encore de compte ?{" "}
            <Link to="/auth/register" data-testid="goto-register" className="font-bold text-[#2BA3A0] underline">
              Créer un compte
            </Link>
          </p>
        </form>

        <div className="mt-8 rounded-2xl bg-white border border-[#E4E0D5] p-4 text-[12px] text-[#0A1426]/80 leading-relaxed">
          <p className="font-bold text-[#0A1426] mb-1">Comptes de démonstration</p>
          <ul className="space-y-1">
            <li>👩 Patiente : <code>mama@bombacare.com</code> / <code>Demo2026!</code></li>
            <li>🤝 Aidant : <code>aidant@bombacare.com</code> / <code>Demo2026!</code></li>
            <li>🩺 Praticien : <code>therapeute@bombacare.com</code> / <code>Bomba2026!</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
